import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Alert, Button, Select, DatePicker, Statistic } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
         BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ScatterChart, Scatter } from 'recharts';
import { emotionMCPClient } from '../../services/mcpClient';
import './EmotionAnalyticsPage.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 情绪数据分析和可视化页面
 */
const EmotionAnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emotionData, setEmotionData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [chartType, setChartType] = useState('trend');

  // 图表颜色配置
  const COLORS = {
    happy: '#52c41a',
    sad: '#1890ff', 
    angry: '#ff4d4f',
    anxious: '#faad14',
    excited: '#722ed1',
    calm: '#13c2c2',
    fear: '#fa8c16',
    surprise: '#eb2f96'
  };

  // 时间范围选项
  const timeRangeOptions = [
    { value: 'today', label: '今天' },
    { value: 'week', label: '本周' },
    { value: 'month', label: '本月' },
    { value: 'quarter', label: '本季度' },
    { value: 'year', label: '本年' }
  ];

  // 图表类型选项
  const chartTypeOptions = [
    { value: 'trend', label: '趋势图' },
    { value: 'distribution', label: '分布图' },
    { value: 'intensity', label: '强度分析' },
    { value: 'calendar', label: '日历视图' },
    { value: 'correlation', label: '关联分析' }
  ];

  // 加载情绪数据
  const loadEmotionData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const query = `获取${getTimeRangeText(selectedTimeRange)}的情绪记录，用于数据分析和可视化`;
      const result = await emotionMCPClient.queryEmotionRecords(query, {
        timeRange: selectedTimeRange,
        emotions: selectedEmotions,
        includeAnalysis: true,
        limit: 200
      });
      
      if (result && result.records) {
        setEmotionData(result.records);
      } else {
        setEmotionData([]);
      }
    } catch (err) {
      console.error('加载情绪数据失败:', err);
      setError('加载数据失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 获取时间范围文本
  const getTimeRangeText = (range) => {
    const rangeMap = {
      today: '今天',
      week: '本周',
      month: '本月', 
      quarter: '本季度',
      year: '本年'
    };
    return rangeMap[range] || '最近';
  };

  // 处理情绪数据用于不同图表
  const processDataForChart = (data, type) => {
    if (!data || data.length === 0) return [];

    switch (type) {
      case 'trend':
        return processTrendData(data);
      case 'distribution':
        return processDistributionData(data);
      case 'intensity':
        return processIntensityData(data);
      case 'calendar':
        return processCalendarData(data);
      case 'correlation':
        return processCorrelationData(data);
      default:
        return data;
    }
  };

  // 处理趋势数据
  const processTrendData = (data) => {
    const dailyData = {};
    
    data.forEach(record => {
      const date = new Date(record.timestamp).toLocaleDateString();
      if (!dailyData[date]) {
        dailyData[date] = { date, count: 0, avgIntensity: 0, totalIntensity: 0 };
      }
      dailyData[date].count += 1;
      dailyData[date].totalIntensity += (record.intensity || 5);
      dailyData[date].avgIntensity = dailyData[date].totalIntensity / dailyData[date].count;
    });

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // 处理分布数据
  const processDistributionData = (data) => {
    const emotionCounts = {};
    
    data.forEach(record => {
      const emotion = record.emotion || 'unknown';
      emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
    });

    return Object.entries(emotionCounts).map(([emotion, count]) => ({
      emotion,
      count,
      percentage: ((count / data.length) * 100).toFixed(1)
    }));
  };

  // 处理强度数据
  const processIntensityData = (data) => {
    const intensityRanges = {
      '1-2': 0, '3-4': 0, '5-6': 0, '7-8': 0, '9-10': 0
    };

    data.forEach(record => {
      const intensity = record.intensity || 5;
      if (intensity <= 2) intensityRanges['1-2']++;
      else if (intensity <= 4) intensityRanges['3-4']++;
      else if (intensity <= 6) intensityRanges['5-6']++;
      else if (intensity <= 8) intensityRanges['7-8']++;
      else intensityRanges['9-10']++;
    });

    return Object.entries(intensityRanges).map(([range, count]) => ({
      range,
      count,
      percentage: ((count / data.length) * 100).toFixed(1)
    }));
  };

  // 处理日历数据 (简化版)
  const processCalendarData = (data) => {
    return processTrendData(data);
  };

  // 处理关联数据
  const processCorrelationData = (data) => {
    return data.map(record => ({
      emotion: record.emotion,
      intensity: record.intensity || 5,
      hour: new Date(record.timestamp).getHours(),
      dayOfWeek: new Date(record.timestamp).getDay()
    }));
  };

  // 渲染统计卡片
  const renderStatCards = () => {
    if (!emotionData || emotionData.length === 0) return null;

    const totalRecords = emotionData.length;
    const avgIntensity = (
      emotionData.reduce((sum, record) => sum + (record.intensity || 5), 0) / totalRecords
    ).toFixed(1);
    
    const emotionCounts = {};
    emotionData.forEach(record => {
      const emotion = record.emotion || 'unknown';
      emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
    });
    
    const mostCommonEmotion = Object.entries(emotionCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || '无';

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="记录总数" value={totalRecords} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="平均强度" value={avgIntensity} precision={1} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="最常见情绪" value={mostCommonEmotion} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="情绪类型" 
              value={Object.keys(emotionCounts).length} 
              suffix="种"
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // 渲染图表
  const renderChart = () => {
    const data = processDataForChart(emotionData, chartType);
    
    if (!data || data.length === 0) {
      return <div style={{ textAlign: 'center', padding: 40 }}>暂无数据</div>;
    }

    switch (chartType) {
      case 'trend':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#1890ff" name="记录数量" />
              <Line type="monotone" dataKey="avgIntensity" stroke="#52c41a" name="平均强度" />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'distribution':
        return (
          <Row gutter={16}>
            <Col span={12}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ emotion, percentage }) => `${emotion} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.emotion] || '#8884d8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Col>
            <Col span={12}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="emotion" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Col>
          </Row>
        );

      case 'intensity':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#faad14" name="记录数量" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'calendar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="count" stackId="1" stroke="#1890ff" fill="#1890ff" />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'correlation':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart data={data}>
              <CartesianGrid />
              <XAxis type="number" dataKey="hour" domain={[0, 23]} name="小时" />
              <YAxis type="number" dataKey="intensity" domain={[1, 10]} name="强度" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Scatter name="情绪记录" data={data} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        );

      default:
        return <div>不支持的图表类型</div>;
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadEmotionData();
  }, [selectedTimeRange, selectedEmotions]);

  return (
    <div className="emotion-analytics-page">
      <div className="page-header">
        <h1>📊 情绪数据分析</h1>
        <p>通过数据可视化深入了解你的情绪模式和变化趋势</p>
      </div>

      {/* 控制面板 */}
      <Card className="control-panel" style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <label>时间范围：</label>
            <Select
              value={selectedTimeRange}
              onChange={setSelectedTimeRange}
              style={{ width: '100%' }}
            >
              {timeRangeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <label>图表类型：</label>
            <Select
              value={chartType}
              onChange={setChartType}
              style={{ width: '100%' }}
            >
              {chartTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <label>筛选情绪：</label>
            <Select
              mode="multiple"
              value={selectedEmotions}
              onChange={setSelectedEmotions}
              placeholder="选择要分析的情绪"
              style={{ width: '100%' }}
            >
              {Object.keys(COLORS).map(emotion => (
                <Option key={emotion} value={emotion}>
                  {emotion}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Button type="primary" onClick={loadEmotionData} loading={isLoading}>
              刷新数据
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 错误提示 */}
      {error && (
        <Alert
          message="数据加载失败"
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 加载状态 */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>正在加载数据...</p>
        </div>
      )}

      {/* 统计概览 */}
      {!isLoading && !error && renderStatCards()}

      {/* 图表展示 */}
      {!isLoading && !error && (
        <Card title={`${chartTypeOptions.find(opt => opt.value === chartType)?.label} - ${getTimeRangeText(selectedTimeRange)}`}>
          {renderChart()}
        </Card>
      )}
    </div>
  );
};

export default EmotionAnalyticsPage;